import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, map, Observable, shareReplay, take } from 'rxjs';
import {
  NewsBannerModel,
  NewsBannerSlideModel,
} from '../../../models/news-banner.model';
import { SeminarCardModel } from '../../../models/seminar-card.model';
import {
  SeminarDateSeriesListModel,
  SeminarModel,
} from '../../../models/seminar.model';
import { SlideInterfaceModel } from '../../../models/slide.model';
import { TrainingCategoryModel } from '../../../models/training-category.model';
import { NewsBannerService } from '../../../services/news-banner.service';
import { SeminarService } from '../../../services/seminar.service';
import { TrainingCategoryService } from '../../../services/training-category.service';
import { UserService } from '../../../services/user.service';
import {
  getSeminarFobiPoints,
  getSeminarLecturerText,
} from '../../../utils/seminar.utils';
import { getUserInitials } from '../../../utils/user.utils';
import { NewsletterSignupDialogComponent } from '../../dialogs/newsletter-signup/newsletter-signup-dialog.component';
import { ContractRevocationDialogComponent } from '../../shared-components/contract-revocation-dialog/contract-revocation-dialog.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: false,
})
export class HomeComponent implements OnInit {
  public seminarCards: SeminarCardModel[] = [];
  public seminars: SeminarModel[] = [];
  private seminarCategories: TrainingCategoryModel[] = [];
  public dataSource$: Observable<any[]>;
  public seminarsLoading = true;
  public seminarCategoriesLoading = true;
  public seminarCardsLoading = true;
  public visibleSeminarCardsCount = 3;
  public showAllSeminarCards = false;
  public newsBannerSlides: NewsBannerSlideModel[] = [];
  public newsBannersLoading = true;

  @HostListener('window:resize')
  onResize() {
    this.calculateVisibleCards();
  }

  constructor(
    public userService: UserService,
    private courseTypeService: TrainingCategoryService,
    private seminarService: SeminarService,
    private newsBannerService: NewsBannerService,
    private router: Router,
    private dialogService: DialogService
  ) {}

  async ngOnInit(): Promise<void> {
    this.calculateVisibleCards();
    await this.getCourseTypes();
    await this.getCurrentUsersSeminarCards();
    await this.getNewsBanners();
    this.getSeminars();
    this.maybeShowNewsletterPopup();
  }

  public cardSlides: SlideInterfaceModel[] = [];

  private async getCurrentUsersSeminarCards(): Promise<void> {
    const email = this.userService.currentUser$.value?.email;
    if (!email) {
      this.seminarCardsLoading = false;
      return;
    }
    return new Promise<void>((resolve, reject) => {
      this.seminarService
        .getSeminarsByUser(email, false)
        .pipe(take(1))
        .subscribe({
          next: response => {
            const seminarDateSeriesList = response;
            this.seminarCards = seminarDateSeriesList.flatMap(
              seminarDateSeries => {
                return (
                  seminarDateSeries.dates
                    // Sort the dates in ascending order
                    .sort((a, b) =>
                      moment(a.startDate).diff(moment(b.startDate))
                    )
                    // Map over the filtered and sorted dates to return the desired structure
                    .map(
                      (courseDate): SeminarCardModel => ({
                        seminarId: seminarDateSeries.seminar.id,
                        seminarDateSeriesId: seminarDateSeries.dateSeriesId,
                        title: seminarDateSeries.seminar.title,
                        lecturer: seminarDateSeries.lecturer1,
                        lecturerInitials: getUserInitials(
                          seminarDateSeries.lecturer1
                        ),
                        date: courseDate.startDate,
                        image: null, // Assuming no image in this context
                        isCanceled: courseDate.isCanceled,
                      })
                    )
                );
              }
            );
            resolve();
            this.seminarCardsLoading = false;
          },
          error: error => {
            this.seminarCardsLoading = false;
            reject(error);
          },
        });
    });
  }

  private getSeminars(): void {
    this.dataSource$ = this.seminarService.getSeminars(false).pipe(
      take(1),
      map(seminarDateSeriesList => {
        this.seminarsLoading = false;
        return seminarDateSeriesList.map(seminarDateSeries => {
          return {
            ...seminarDateSeries,
            title: seminarDateSeries.seminar.title,
            dates: seminarDateSeries.dates,
            lecturer: getSeminarLecturerText(seminarDateSeries),
            fobiPoints: getSeminarFobiPoints(seminarDateSeries),
            status: this.getStatus(seminarDateSeries),
          };
        });
      }),
      catchError(error => {
        this.seminarsLoading = false;
        console.error('Error fetching seminars', error);
        return [];
      }),
      shareReplay(1) // Cache the result
    );
  }

  private getStatus(seminarDateSeries: SeminarDateSeriesListModel): string {
    if (
      seminarDateSeries.dates.find(item =>
        moment(item.startDate).isBefore(moment())
      ) &&
      seminarDateSeries.dates.find(item =>
        moment(item.endDate).isAfter(moment())
      )
    ) {
      return 'Läuft';
    }
    return `${Math.max(0, seminarDateSeries.seminar.maxParticipants - seminarDateSeries.participantCount)} frei`;
  }

  private async getCourseTypes(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.courseTypeService.getAllTrainingCategories().subscribe(types => {
        this.seminarCategories = types;
        this.cardSlides = types.map(type => ({
          title: type.name,
          url: type.titlePicture ?? null,
          details:
            type.courseCount == 1
              ? `${type.courseCount} Seminar`
              : `${type.courseCount} Seminare`,
          color50: type.color50,
          color0: type.color0,
          color70: type.color70,
          color80: type.color80,
          color90: type.color90,
        }));
        resolve();
        this.seminarCategoriesLoading = false;
      }),
        catchError(error => {
          this.seminarCategoriesLoading = false;
          console.error('Error fetching course types', error);
          reject(error);
          return [];
        }),
        shareReplay(1);
    });
  }

  private async getNewsBanners(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.newsBannerService
        .getAllNewsBanner()
        .subscribe((newsBanners: NewsBannerModel[]) => {
          this.newsBannerSlides =
            this.newsBannerService.mapNewsBannersToSlides(newsBanners);
          this.newsBannersLoading = false;
          resolve();
        }),
        catchError(error => {
          console.error('Error fetching news banners', error);
          this.newsBannersLoading = false;
          reject(error);
          return [];
        });
    });
  }

  public onCardClicked(card: SlideInterfaceModel) {
    const selectedCourseType = this.seminarCategories.find(
      type => type.name === card.title
    );
    if (selectedCourseType) {
      this.router.navigate([
        'advancedtraining',
        btoa(String(selectedCourseType.id)),
      ]);
    }
  }

  public onSeminarCardClicked(seminarCard: SeminarCardModel): void {
    this.router.navigate([
      'seminars',
      btoa(String(seminarCard.seminarId)),
      btoa(String(seminarCard.seminarDateSeriesId)),
    ]);
  }

  /**
   * Calculate the amount of visible seminar cards based on the window width and the card width
   * The card width is 328px and the margin between the cards is 16px
   * The container width is the window width minus the padding of 24px on each side up to a maximum of 1300px
   */
  public calculateVisibleCards(): void {
    const containerWidth =
      (window.innerWidth < 1300 ? window.innerWidth : 1300) - 24 * 2; // Get the window width
    this.visibleSeminarCardsCount = Math.max(
      1,
      Math.floor(containerWidth / (328 + 16))
    );
  }

  public toggleShowAll(): void {
    this.showAllSeminarCards = !this.showAllSeminarCards;
  }

  get displayedCards() {
    return this.showAllSeminarCards
      ? this.seminarCards
      : this.seminarCards.slice(0, this.visibleSeminarCardsCount);
  }

  get seminarCardSkeletons(): number[] {
    return Array.from({ length: this.visibleSeminarCardsCount });
  }

  get hasUpcomingSeminars(): boolean {
    return !this.seminarCardsLoading && this.seminarCards.length > 0;
  }

  get showNewsletterSection(): boolean {
    return !Boolean(this.userService.getUser()?.newsletterSubscribed);
  }

  private maybeShowNewsletterPopup(): void {
    const user = this.userService.currentUser$.value;
    if (!user?.id || user.newsletterSubscribed) {
      return;
    }

    if (localStorage.getItem(`inwi_newsletter_popup_dismissed`) === 'true') {
      return;
    }

    const dialogRef = this.dialogService.open(NewsletterSignupDialogComponent, {
      header: 'Jetzt für Newsletter anmelden',
      width: '600px',
      breakpoints: {
        '768px': '95vw',
      },
    });

    dialogRef.onClose.subscribe((subscribed: boolean) => {
      if (subscribed) {
        localStorage.setItem(`inwi_newsletter_popup_dismissed`, 'true');
      }
    });
  }

  public openContractRevocationDialog(): void {
    const dialogRef = this.dialogService.open(
      ContractRevocationDialogComponent,
      {
        width: '600px',
        breakpoints: {
          '768px': '95vw',
        },
      }
    );
    dialogRef.onClose.subscribe((success: boolean) => {
      if (success) {
        this.seminarCardsLoading = true;
        this.getCurrentUsersSeminarCards();
      }
    });
  }
}
